import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  getMetaConnection,
  startMetaAuth,
  disconnectMeta,
  MetaConnection,
} from "../../../../database/community/metaConnection";

// First-party Instagram + Facebook panel for the Media Studio. Talks to OUR Meta
// app: connect via OAuth (one Facebook login links the Page + its IG Business
// account). The per-project IG/FB publish actions live in
// publish/PublishButtons. Mirrors TikTokConnectPanel / LinkedInConnectPanel.

const SmallButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(SmallButton)`
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;

  &:hover:not(:disabled) {
    color: #fff;
    filter: brightness(1.05);
  }
`;

const PanelRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const PanelLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin-right: var(--space-1);
`;

const ProfileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

const Muted = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

const Spacer = styled.span`
  flex: 1 1 auto;
`;

export const MetaConnectPanel: React.FC<{
  communityId?: string;
  onConnectedChange?: (state: { instagram: boolean; page: boolean }) => void;
}> = ({ communityId, onConnectedChange }) => {
  const [config, setConfig] = useState<MetaConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const cfg = await getMetaConnection(communityId);
      setConfig(cfg);
      onConnectedChange?.({
        instagram: Boolean(cfg.connected && cfg.igUsername),
        page: Boolean(cfg.connected && cfg.pageName),
      });
    } catch (e: any) {
      setError(e?.message || "Could not load the Instagram/Facebook status.");
    } finally {
      setLoading(false);
    }
  }, [communityId, onConnectedChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleConnect = async () => {
    if (!communityId) return;
    setBusy(true);
    setError("");
    try {
      const { url } = await startMetaAuth(communityId, "facebook");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setError(e?.message || "Could not start the connection.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!communityId) return;
    if (!window.confirm("Disconnect this Instagram/Facebook account?")) return;
    setBusy(true);
    setError("");
    try {
      await disconnectMeta(communityId);
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not disconnect.");
    } finally {
      setBusy(false);
    }
  };

  if (!communityId) return null;

  const connected = Boolean(config?.connected);
  const stale = connected && config?.tokenValid === false;

  return (
    <PanelRoot>
      <PanelLabel>Instagram / Facebook</PanelLabel>
      {loading && !config ? (
        <Muted>Checking…</Muted>
      ) : connected ? (
        <>
          {config?.igUsername ? <ProfileChip>IG @{config.igUsername}</ProfileChip> : null}
          {config?.pageName ? <ProfileChip>{config.pageName}</ProfileChip> : null}
          {!config?.igUsername ? (
            <Muted>No IG Business account linked to this Page.</Muted>
          ) : null}
          {stale ? (
            <Muted style={{ color: "var(--color-danger)" }}>
              Token invalid — reconnect.
            </Muted>
          ) : null}
        </>
      ) : (
        <Muted>No Instagram/Facebook account connected yet.</Muted>
      )}
      <Spacer />
      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}
      {connected && !stale ? (
        <>
          <SmallButton type="button" onClick={handleConnect} disabled={busy}>
            {busy ? "Working…" : "Reconnect"}
          </SmallButton>
          <SmallButton type="button" onClick={handleDisconnect} disabled={busy}>
            Disconnect
          </SmallButton>
        </>
      ) : (
        <PrimaryButton type="button" onClick={handleConnect} disabled={busy}>
          {busy ? "Opening…" : connected ? "Reconnect" : "Connect Instagram / Facebook"}
        </PrimaryButton>
      )}
    </PanelRoot>
  );
};

export default MetaConnectPanel;
